import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image/Image'
import Link from 'UIKit/Link/Link'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text/Text'
import BigNumber from 'bignumber.js'
import { RouteConfig } from 'config/constants/route'
import { ProfileTierInfo } from 'config/types/profile'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Icons } from 'svgs'
import { revalidateHunnyLevel } from 'utils'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { useLevelUpBonus } from './hooks'
import {
  ModalWrapper,
  ProgressBar,
  ScaleVariantBonus,
  StyledArrowButton,
  StyledButton,
  Wrapper,
  WrapperContentDropdown,
} from './styled'

const InfoTierItem: React.FC<{
  tier: ProfileTierInfo
  title: string
  description?: ReactNode | string
  isModal?: boolean
}> = ({ tier, title, description, isModal }) => {
  return (
    <Flex>
      <Image src={tier.logoUrl} width={isModal ? 32 : 24} height={isModal ? 32 : 24} />
      <Column ml="8px">
        <Text color="textAlt1" fontSize={['12px', '', '10px']}>
          {title}
        </Text>
        <RowMiddle mt="8px">
          <Text bold fontSize={['16px', '', '12px']}>
            ${getFullDisplayBalance(new BigNumber(tier.bonusUsd), 0, 2)}
          </Text>
          <Text color="textTertiary" fontSize={['16px', '', '12px']} ml="4px">
            <Trans>Bonus</Trans>
          </Text>
        </RowMiddle>
        {description && <Column mt="8px">{description}</Column>}
      </Column>
    </Flex>
  )
}

const LevelUpContent: React.FC<{
  userTier: ProfileTierInfo
  userNextTier: ProfileTierInfo
  wagerUsdToNextLevel: string
  isModal?: boolean
  onDismiss?: () => void
}> = ({ userNextTier, userTier, wagerUsdToNextLevel, isModal, onDismiss = () => {} }) => {
  const { t } = useTranslation()

  return (
    <Column position="relative">
      {userTier && (
        <InfoTierItem
          isModal={isModal}
          title={t('Current Level')}
          tier={userTier}
          description={
            userNextTier && (
              <RowMiddle>
                <Text fontSize={['12px', '', '10px']} color="textAlt1">
                  💡{' '}
                  <Trans
                    i18nKey="updateWagerLevelDescription"
                    components={{
                      highlight_tag: (
                        <Text as="span" fontSize={['12px', '', '10px']} color="textSubtle" fontWeight={600} />
                      ),
                    }}
                    values={{
                      wagerUsdToNextLevel,
                      level: revalidateHunnyLevel(userNextTier?.level),
                    }}
                  />
                </Text>
              </RowMiddle>
            )
          }
        />
      )}
      {userNextTier && (
        <Box mt={['20px', '', '12px']}>
          <RowCenter
            position="absolute"
            width="24px"
            style={{
              transform: 'translate(11%,-100%)',
            }}
          >
            <Icons.DashedLineIcon height={'36px'} />
          </RowCenter>
          <InfoTierItem isModal={isModal} tier={userNextTier} title={t('Next Level')} />
        </Box>
      )}
      <Column width="100%">
        <StyledButton
          as={Link}
          href={RouteConfig.Profile}
          width={['100%', '', 'max-content']}
          mt={['40px', '', '16px']}
          primary={isModal}
          fontSize={['14px', '', '12px']}
          onClick={onDismiss}
        >
          <RowCenter width="100%" justifyContent={['center !important', '', 'flex-start !important']}>
            <Trans>View Details</Trans>
          </RowCenter>
        </StyledButton>
      </Column>
    </Column>
  )
}

export const LevelUpDropdown: React.FC<{
  userTier: ProfileTierInfo
  userNextTier: ProfileTierInfo
  wagerUsdToNextLevel: string
}> = ({ userNextTier, userTier, wagerUsdToNextLevel }) => {
  return (
    <WrapperContentDropdown>
      <LevelUpContent userNextTier={userNextTier} userTier={userTier} wagerUsdToNextLevel={wagerUsdToNextLevel} />
    </WrapperContentDropdown>
  )
}

export const LevelUpModal: React.FC<ModalProps> = ({ onDismiss = () => {}, ...props }) => {
  const { wagerUsdToNextLevel, userTier, userNextTier } = useLevelUpBonus()
  return (
    <ModalWrapper title="Level up Bonus" onDismiss={onDismiss} {...props}>
      <LevelUpContent
        userNextTier={userNextTier}
        userTier={userTier}
        wagerUsdToNextLevel={wagerUsdToNextLevel}
        isModal
        onDismiss={onDismiss}
      />
    </ModalWrapper>
  )
}

const LevelUpBonus: React.FC<ScaleVariantBonus & BoxProps> = ({ ...props }) => {
  const { t } = useTranslation()
  const { wager, wagerUsdToNextLevel, bonusUsdNextLevel, userTier, userNextTier } = useLevelUpBonus()
  const { isDesktop } = useMatchBreakpoints()

  const [onPresent] = useModal(LevelUpModal)

  return (
    userNextTier && (
      <>
        <Wrapper
          dropdownContent={
            isDesktop && (
              <LevelUpDropdown
                userTier={userTier}
                userNextTier={userNextTier}
                wagerUsdToNextLevel={wagerUsdToNextLevel}
              />
            )
          }
          dropdownPosition="bottom-left"
          title={t('Level up Bonus')}
          onClick={() => {
            if (!isDesktop) {
              onPresent()
            }
          }}
          style={{
            cursor: 'auto',
          }}
          {...props}
        >
          <RowBetween>
            <ProgressBar progress={wager?.percentage}>
              <RowMiddle>
                <Box minWidth="24px">
                  <Image src="/images/bonus/level-up-bonus.png" width={24} height={24} original />
                </Box>
                <RowBetween>
                  <RowMiddle ml="4px">
                    <Text ellipsis={1} fontSize={['14px', '', '12px']} bold mr="4px">
                      ${bonusUsdNextLevel} (VIP {revalidateHunnyLevel(userNextTier?.level)})
                    </Text>
                  </RowMiddle>
                  <Text fontSize={['14px', '', '12px']} bold ml="8px">
                    {wager?.percentage}%
                  </Text>
                </RowBetween>
              </RowMiddle>
            </ProgressBar>
            <StyledArrowButton ml="8px" disabled />
          </RowBetween>
        </Wrapper>
        {/* <Box background={colors.background} height="1px" width="100%" my="12px" /> */}
      </>
    )
  )
}

export default LevelUpBonus
